/**
 * RichTextField — one block's rich-text input (Notion-style per-block editing). Controlled by `runs`; reports text
 * edits as [start, end) → inserted, the selection, and editor keys (Enter, Backspace at start, Tab, ↑/↓ at the
 * first/last line, ⌘B, ⌘K, ⌘Z, …) so the view model decides what they do. Remote carets render inline.
 *
 * Web: ProseMirror (one EditorView per block), loaded lazily with the editor so chat never pays for it; until it
 * loads, the runs render read-only in the same style. iOS: TextEditor + AttributedString; Android: BasicTextField +
 * TextFieldState (see docs/porting/primitives.md, primitive-gaps.md).
 */
import type { PickedFile } from "@bundle/core";
import { lazy, Suspense, useRef, type ReactNode } from "react";
import { color as colorVar, cx, fontSize, fontWeight, lineHeight, size as sizeVar, type ColorToken, type FontToken, type SizeToken, type WeightToken } from "./tokens.ts";
import "./richTextField.css";

/** A run of text with the same marks. Mentions are one U+FFFC character with a label to show. */
export type RichRun = {
  text: string;
  bold?: boolean;
  italic?: boolean;
  strike?: boolean;
  code?: boolean;
  underline?: boolean;
  link?: string;
  mention?: RichMention;
};

/** Message chip looks (InlineText `chip`): a mention drawn as one of them shows its label as is (`@Ada`, `#design`). */
export type RichChip = "user" | "agent" | "channel" | "broadcast" | "self" | "group";

export type RichMention = {
  kind: "user" | "page" | "date" | "channel" | "broadcast";
  id?: string;
  label: string;
  /** Draw it as the message chip (chat composer); absent = the docs mention look. */
  chip?: RichChip;
  /** What copying it puts on the clipboard as plain text (a chat token like `<@id>`); absent = the label. */
  copyText?: string;
};

export type RichTextKey =
  | "enter" | "shiftEnter" | "backspace" | "delete" | "tab" | "shiftTab" | "up" | "down" | "shiftUp" | "shiftDown"
  | "escape" | "bold" | "italic" | "strike" | "code" | "underline" | "link" | "undo" | "redo" | "selectAll" | "moveUp" | "moveDown";

/**
 * Slack's composer-only formatting shortcuts, reported through `onFormatKey` (the docs editor doesn't take them):
 * ⌘⇧C inline code, ⌘⇧U link, ⌘⌥⇧C code block, ⌘⇧7 numbered list, ⌘⇧8 bulleted list, ⌘⇧9 quote.
 */
export type RichFormatKey = "code" | "link" | "codeBlock" | "orderedList" | "bulletList" | "quote";

export type RichTextKeyEvent = {
  key: RichTextKey;
  start: number;
  end: number;
  /** The caret is on the first / last visual line. */
  firstLine: boolean;
  lastLine: boolean;
};

export type RichTextCaret = { key: string; offset: number; name: string };

export type RichTextFieldProps = {
  runs: RichRun[];
  /** [start, end) of the previous text was replaced by `inserted` (typing, IME, cut, drop). */
  onChange: (start: number, end: number, inserted: string) => void;
  /** Editor keys; return true when handled (the field then does nothing). */
  onKey?: (e: RichTextKeyEvent) => boolean;
  /** Slack's composer formatting shortcuts (RichFormatKey) at the selection [start, end); true when handled. */
  onFormatKey?: (key: RichFormatKey, start: number, end: number) => boolean;
  onSelect?: (start: number, end: number) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  /** Plain-text paste; return true when handled. */
  onPaste?: (text: string, start: number, end: number) => boolean;
  /** Pasted files (screenshots, copied files); absent = the field ignores them. */
  onPasteFiles?: (files: PickedFile[]) => void;
  /** Focus the field with the caret at `offset` (or the selection [offset, end)) whenever `seq` changes. */
  focus?: { offset: number; end?: number; seq: number };
  placeholder?: string;
  /** Focus the field (caret at the end) when it mounts. */
  autoFocus?: boolean;
  /** Grows up to this height, then scrolls (the chat composer). */
  maxHeight?: SizeToken;
  /**
   * Rendered while the editor chunk loads (an editable plain field, so typing never waits); absent = the runs,
   * read-only. Focus in it moves to the editor once it loads.
   */
  fallback?: ReactNode;
  /** Show the placeholder only while focused (an empty paragraph's "Type '/' for commands"). */
  placeholderOnFocus?: boolean;
  variant?: FontToken;
  weight?: WeightToken;
  color?: ColorToken;
  /** Monospaced, keeps newlines (code blocks). */
  mono?: boolean;
  /** Strike the whole text (a checked to-do). */
  struck?: boolean;
  readOnly?: boolean;
  /** Other people's carets in this block. */
  carets?: RichTextCaret[];
  onPressMention?: (kind: "user" | "page" | "date", id: string | undefined) => void;
  accessibilityLabel?: string;
  testID: string;
};

const loadEditor = () => import("./RichTextEditor.tsx");
const Editor = lazy(loadEditor);

/** Starts loading the editor chunk (the chat composer calls it on mount so the field is ready before typing). */
export function preloadRichTextField(): void {
  void loadEditor();
}

/** Shared look of the editable and static renderings. */
export function richTextStyle(p: RichTextFieldProps): { className: string; style: Record<string, string | undefined> } {
  const variant = p.variant ?? "body";
  return {
    className: cx("b-rtf", p.mono === true && "b-rtf-mono", p.struck === true && "b-rtf-struck", p.placeholderOnFocus === true && "b-rtf-focus-only"),
    style: {
      fontSize: fontSize(variant),
      lineHeight: lineHeight(variant),
      fontWeight: p.weight === undefined ? undefined : fontWeight(p.weight),
      color: colorVar(p.color ?? "text.primary"),
      maxHeight: p.maxHeight === undefined ? undefined : sizeVar(p.maxHeight),
      overflowY: p.maxHeight === undefined ? undefined : "auto",
    },
  };
}

/** Read-only rendering of runs (while the editor chunk loads, and for read-only fallbacks). */
export function StaticRichText(p: RichTextFieldProps) {
  const { className, style } = richTextStyle(p);
  const empty = p.runs.every((r) => r.text === "");
  return (
    <div className={cx(className, empty && "b-rtf-empty")} style={style} data-placeholder={p.placeholderOnFocus === true ? undefined : p.placeholder} data-testid={p.testID}>
      {p.runs.map((r, i) =>
        r.mention !== undefined ? (
          <span key={i} className={r.mention.chip === undefined ? `b-mention b-mention-${r.mention.kind}` : `b-chip b-chip-${r.mention.chip} b-rtf-chip`}>
            {r.mention.label}
          </span>
        ) : (
          <span key={i} className={cx(r.bold === true && "b-run-bold", r.italic === true && "b-run-italic", r.strike === true && "b-run-strike", r.code === true && "b-run-code", r.underline === true && "b-run-underline", r.link !== undefined && "b-rtf-link")}>
            {r.text}
          </span>
        ),
      )}
    </div>
  );
}

export function RichTextField(p: RichTextFieldProps) {
  const box = useRef<HTMLDivElement>(null);
  const fallback =
    p.fallback === undefined ? (
      <StaticRichText {...p} />
    ) : (
      <div ref={box} className="b-rtf-fallback">
        {p.fallback}
      </div>
    );
  return (
    <Suspense fallback={fallback}>
      <Editor {...p} takeFocus={() => box.current !== null && box.current.contains(document.activeElement)} />
    </Suspense>
  );
}
